import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';

// ----------------------------------------------------------------------

/**
 * @Docs
 * @see https://day.js.org/docs/en/display/format
 */

/**
 * Default timezones
 * @see https://day.js.org/docs/en/timezone/set-default-timezone#docsNav
 *
 */

/**
 * UTC
 * @see https://day.js.org/docs/en/plugin/utc
 * @install
 * import utc from 'dayjs/plugin/utc';
 * dayjs.extend(utc);
 * @usage
 * dayjs().utc().format()
 */

dayjs.extend(duration);
dayjs.extend(relativeTime);

export const formatPatterns = {
  dateTime: 'DD MMM YYYY h:mm a', // 17 Apr 2022 12:00 am
  date: 'DD MMM YYYY', // 17 Apr 2022
  time: 'h:mm a', // 12:00 am
  split: {
    dateTime: 'DD/MM/YYYY h:mm a', // 17/04/2022 12:00 am
    date: 'DD/MM/YYYY', // 17/04/2022
  },
  paramCase: {
    dateTime: 'DD-MM-YYYY h:mm a', // 17-04-2022 12:00 am
    date: 'DD-MM-YYYY', // 17-04-2022
  },
};

const isValidDate = (date) => date && dayjs(date).isValid();

export const fToday = (template) => dayjs(new Date()).startOf('day').format(template);

/**
 * @output 17 Apr 2022 12:00 am
 */
export function fDateTime(date, template) {
  if (!isValidDate(date)) return null;

  return dayjs(date).format(template ?? formatPatterns.dateTime);
}

/**
 * @output 17 Apr 2022
 */
export function fDate(date, template) {
  if (!isValidDate(date)) return null;

  return dayjs(date).format(template ?? formatPatterns.date);
}

/**
 * @output 2019-01-25T02:00:00.000Z
 */
export function fDateISO(date = new Date()) {
  if (!isValidDate(date)) return null;

  return dayjs(date).toISOString();
}

/**
 * @output 12:00 am
 */
export function fTime(date, template) {
  if (!isValidDate(date)) return null;

  return dayjs(date).format(template ?? formatPatterns.time);
}

/**
 * @output 1713250100
 */
export function fTimestamp(date) {
  if (!isValidDate(date)) return null;

  return dayjs(date).valueOf();
}

/**
 * @output a few seconds, 2 years
 */
export function fToNow(date) {
  if (!isValidDate(date)) return null;

  return dayjs(date).toNow(true);
}

/**
 * @output a 2 years ago
 */
export function fUntilNow(date) {
  if (!isValidDate(date)) return null;

  return dayjs().to(dayjs(date));
}

/**
 * @output boolean
 */
export function fIsBetween(inputDate, startDate, endDate) {
  if (!isValidDate(inputDate) || !isValidDate(startDate) || !isValidDate(endDate)) {
    return false;
  }

  const formattedInputDate = fTimestamp(inputDate);
  const formattedStartDate = fTimestamp(startDate);
  const formattedEndDate = fTimestamp(endDate);

  if (formattedInputDate === null || formattedStartDate === null || formattedEndDate === null) {
    return false;
  }

  return formattedInputDate >= formattedStartDate && formattedInputDate <= formattedEndDate;
}

/**
 * @output boolean
 */
export function fIsAfter(startDate, endDate = dayjs()) {
  if (!isValidDate(startDate) || !isValidDate(endDate)) {
    return false;
  }

  return dayjs(startDate).isAfter(endDate);
}

/**
 * @output boolean
 */
export function fIsSame(startDate, endDate, unitToCompare) {
  if (!isValidDate(startDate) || !isValidDate(endDate)) {
    return false;
  }

  return dayjs(startDate).isSame(endDate, unitToCompare ?? 'year');
}

/**
 * @output
 * Same day: 26 Apr 2024
 * Same month: 25 - 26 Apr 2024
 * Same month: 25 - 26 Apr 2024
 * Same year: 25 Apr - 26 May 2024
 */
export function fDateRangeShortLabel(startDate, endDate, initial) {
  if (!isValidDate(startDate) || !isValidDate(endDate) || fIsAfter(startDate, endDate)) {
    return null;
  }

  let label = `${fDate(startDate)} - ${fDate(endDate)}`;

  if (initial) return label;

  const isSameYear = fIsSame(startDate, endDate, 'year');
  const isSameMonth = fIsSame(startDate, endDate, 'month');
  const isSameDay = fIsSame(startDate, endDate, 'day');

  if (isSameYear && !isSameMonth) {
    label = `${fDate(startDate, 'DD MMM')} - ${fDate(endDate)}`;
  } else if (isSameYear && isSameMonth && !isSameDay) {
    label = `${fDate(startDate, 'DD')} - ${fDate(endDate)}`;
  } else if (isSameYear && isSameMonth && isSameDay) {
    label = `${fDate(endDate)}`;
  }

  return label;
}

export function fAdd({
  years = 0,
  months = 0,
  days = 0,
  hours = 0,
  minutes = 0,
  seconds = 0,
  milliseconds = 0,
  startDate = new Date(),
}) {
  const result = dayjs(startDate)
    .add(
      dayjs.duration({
        years,
        months,
        days,
        hours,
        minutes,
        seconds,
        milliseconds,
      })
    )
    .format();

  return result;
}

/**
 * @output 2024-05-28T05:55:31+00:00
 */
export function fSub({
  years = 0,
  months = 0,
  days = 0,
  hours = 0,
  minutes = 0,
  seconds = 0,
  milliseconds = 0,
}) {
  const result = dayjs()
    .subtract(
      dayjs.duration({
        years,
        months,
        days,
        hours,
        minutes,
        seconds,
        milliseconds,
      })
    )
    .format();

  return result;
}

/**
 * @output 31
 */
export function fDaysInMonth(date) {
  if (!isValidDate(date)) return null;

  return dayjs(date).daysInMonth();
}

/**
 * @output MM/YYYY
 */
export function fExpiryDate(date) {
  if (typeof date === 'string' && date.includes('/')) return dayjs(date, 'MM/YYYY');

  if (!isValidDate(date)) return null;

  return dayjs(date).format('MM/YYYY');
}

export function fPadStartDate(number) {
  if (!number) return '01';

  return number.toString().padStart(2, '0');
}

/**
 * @param {function} t function to translate the language.
 * @returns {Array<{}>} an array of months with their corresponding values.
 * @example [{ label: 'Jan', value: '01' }, { label: 'Feb', value: '02' }, ...]
 */
export const getMonthsArray = (t = (value) => value) => [
  { label: t('Jan'), value: '01' },
  { label: t('Feb'), value: '02' },
  { label: t('Mar'), value: '03' },
  { label: t('Apr'), value: '04' },
  { label: t('May'), value: '05' },
  { label: t('Jun'), value: '06' },
  { label: t('Jul'), value: '07' },
  { label: t('Aug'), value: '08' },
  { label: t('Sep'), value: '09' },
  { label: t('Oct'), value: '10' },
  { label: t('Nov'), value: '11' },
  { label: t('Dec'), value: '12' },
];

export function getYearsArray(currentYear = new Date().getFullYear()) {
  const yearsArray = new Set();

  for (let year = 2018; year <= currentYear; year += 1) {
    yearsArray.add(year);
  }

  return [...yearsArray];
}

export function getArrayLast7Years(currentYear = new Date().getFullYear()) {
  const yearsArray = new Set();

  for (let index = 0; index < 7; index += 1) {
    yearsArray.add(currentYear - index);
  }

  return [...yearsArray];
}

/**
 * @output ['YYYY', 'MM', 'DD']
 */
export const getDateArray = () => fToday('YYYY-MM-DD').split('-');

/**
 * Slice an array of numbers into weeks and calculate the total of each week.
 * @param {Array<number>} [array=[]] Array of numbers to be sliced.
 * @returns {{ totalsArray: Array<number>; weeksArray: Array<Array<number>>; }}
 * @example
 * input: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1]
 * output: {
 *  totalsArray: [7, 7, 7, 7],
 *  weeksArray: [[1, 1, 1, 1, 1, 1, 1], [1, 1, 1, 1, 1, 1, 1], [1, 1, 1, 1, 1, 1, 1], [1, 1, 1, 1, 1, 1, 1]]
 * }
 */
export function sliceArrayByWeeks(array = []) {
  const weeksArray = [];

  const totalsArray = [];

  const parts = array.length === 28 ? 7 : 8;

  for (let index = 0; index < array.length; index += parts) {
    const weekArray = array.slice(index, index + parts);

    const totalWeek = weekArray.reduce((total, num) => total + num, 0);

    weeksArray.push(weekArray);

    totalsArray.push(totalWeek);
  }

  return { weeksArray, totalsArray };
}
